'use client';

import { useState } from 'react';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { useResumeSearchAlerts } from '@hooks/filters/useResumeSearchAlerts';
import { fetchSearchData } from '@components/templates/job/search/searchFetcher';
import { ExternalGetQueryFunction } from '@components/common/Forms';
import { JobSearchGroup } from '@type/job-search';
import { DocumentInterface } from '@type/v1-api-types';

/**
 * Talent-search keyword suggestions, shared by the navbar SearchSection and the bottom
 * TalentSearchPrompt: debounced job-title/employer autocomplete merged with the user's
 * saved resume-search-alert keywords as the pre-typing default list.
 *
 * - `suggestions`: typed-ahead results when the last fetch returned any, otherwise the
 *   saved alert keywords (both `{ suggestion: string }`-shaped, JobSearchGroup).
 * - `fetchSuggestions(keyword)`: debounced (350ms) — call on every input change.
 * - `mutateSearchAlerts`: revalidate the saved-alerts list after a submitted search.
 */
export const useTalentSearchSuggestions = (locale: Locale) => {
  const [typedSuggestions, setTypedSuggestions] = useState<JobSearchGroup | undefined>(undefined);

  const { mutateSearchAlerts, alertsKeywords } = useResumeSearchAlerts({ locale });

  const fetchSuggestions = useDebounce<ExternalGetQueryFunction>((keyword: string) => fetchSearchData(keyword, '', '').then((res) => {
    setTypedSuggestions(res.jobSearchGroup.filter((item) => !!(item as DocumentInterface)?.suggestion));
  }), 350);

  const suggestions = typedSuggestions && typedSuggestions.length > 0 ? typedSuggestions : alertsKeywords;

  return { suggestions, fetchSuggestions, mutateSearchAlerts };
};
